import { useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux';
import { useVendorAuth } from '../context/VendorAuthContext';
import { fetchBusinessesByVendor } from '../state/slices/businessSlice';
import ImageCropUploader from './ImageCropUploader';
import { checkFieldsForProfanity } from '../helper/profanityFilter';

const CATEGORY_OPTIONS = [
    "Attractions",
    "Beer / Drinks",
    "Accommodations",
    "Coffee Shops",
    "Dining",
    "Retail",
    "Transportation",
    "Golf / Mini",
    "Tours / Excursions",
    "Spa / Health",
    "Museums",
    "Misc",
];

const CategoryFeaturedCouponRegistration = () => {
    const { vendor } = useVendorAuth();
    const dispatch = useDispatch();
    const businesses = useSelector((state) => state.businesses.vendorBusinesses);

    const [couponData, setCouponData] = useState({
        vendor_id: vendor?.id || "",
        business_id: "",
        title: "",
        description: "",
        discount_category: "",
        coupon_type: "Category-Featured",
        saving_percentage: "",
        dollar_savings_amount: "",
        max_redemptions_per_user: 1,
        front_page_coupon_type: false,
        featured_category: "",
        active: true,
        image_url: "",
        image_url_2: "",
        image_url_3: "",
    });

    useEffect(() => {
        if (!vendor?.id) return;
        setCouponData((prev) => ({ ...prev, vendor_id: vendor.id }));
        dispatch(fetchBusinessesByVendor(vendor.id));
    }, [vendor, dispatch]);

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [showPreviewModal, setShowPreviewModal] = useState(false);

    const isFormComplete = Boolean(
        couponData.business_id &&
        couponData.title &&
        couponData.description &&
        couponData.discount_category &&
        couponData.saving_percentage &&
        couponData.dollar_savings_amount &&
        couponData.featured_category
    );

    const selectedBusiness = businesses.find(b => b.id === parseInt(couponData.business_id)) || null;
    const uploadedPreviewImages = [couponData.image_url, couponData.image_url_2, couponData.image_url_3].filter(Boolean);
    const previewImages = uploadedPreviewImages.length > 0 ? uploadedPreviewImages : [
        "assets/images/thumbs/product-details-two-thumb1.png",
        "assets/images/thumbs/product-details-two-thumb2.png",
        "assets/images/thumbs/product-details-two-thumb3.png",
    ];
    const previewSpecs = [
        ["Coupon Type", "Category-Featured"],
        ["Featured Category", couponData.featured_category],
        ["Placement Duration", "6 months from payment date"],
        ["Coupon Name", couponData.title],
        ["Discount Category", couponData.discount_category],
        ["Savings Percentage", couponData.saving_percentage ? `${couponData.saving_percentage}%` : "—"],
        ["Dollar Savings Amount", couponData.dollar_savings_amount ? `$${couponData.dollar_savings_amount}` : "—"],
        ["Max Redemptions Per Subscription", String(couponData.max_redemptions_per_user)],
    ];

    const sectionRef = useRef(null);
    const previewColRef = useRef(null);
    const innerRef = useRef(null);

    useEffect(() => {
        const FIXED_TOP = 120;
        const STICK_AFTER = 350;
        const update = () => {
            const section = sectionRef.current;
            const col = previewColRef.current;
            const inner = innerRef.current;
            if (!section || !col || !inner) return;

            const sectionRect = section.getBoundingClientRect();
            const innerHeight = inner.offsetHeight;

            if (sectionRect.top <= FIXED_TOP - STICK_AFTER && sectionRect.bottom >= innerHeight + FIXED_TOP) {
                inner.style.position = 'fixed';
                inner.style.top = FIXED_TOP + 'px';
                inner.style.left = col.getBoundingClientRect().left + 'px';
                inner.style.width = col.offsetWidth + 'px';
            } else {
                inner.style.position = '';
                inner.style.top = '';
                inner.style.left = '';
                inner.style.width = '';
            }
        };

        window.addEventListener('scroll', update, { passive: true });
        window.addEventListener('resize', update, { passive: true });
        update();
        return () => {
            window.removeEventListener('scroll', update);
            window.removeEventListener('resize', update);
        };
    }, []);

    const handleChange = (e) => {
        setCouponData({ ...couponData, [e.target.name]: e.target.value });
    };

    const handleCouponRegister = async (e) => {
        e.preventDefault();
        const profanityError = checkFieldsForProfanity({
            "Title": couponData.title,
            "Description": couponData.description,
        });
        if (profanityError) { setError(profanityError); return; }
        setLoading(true);
        setError("");

        try {
            localStorage.setItem("pendingCategoryFeaturedCoupon", JSON.stringify(couponData));

            const response = await fetch(`${process.env.REACT_APP_API_URL}/api/create-checkout-session`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    items: [{ title: "Category-Featured Coupon Package", price: 199.99 }],
                    vendor_id: vendor?.id,
                    purchase_type: "category_featured_coupon",
                }),
            });

            const data = await response.json();
            if (!response.ok) {
                localStorage.removeItem("pendingCategoryFeaturedCoupon");
                setError(data.error || "Checkout session creation failed");
                return;
            }

            window.location.href = data.checkout_url;
        } catch (err) {
            console.error(err);
            localStorage.removeItem("pendingCategoryFeaturedCoupon");
            setError("Server error. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    return (
    <>
    <section className="vendor-registration py-80" ref={sectionRef}>
        <div className="container container-lg">

            {/* Header */}
            <div className="text-center mb-48">
                <h2 className="mb-16">Create a Category-Featured Coupon</h2>
                <p className="text-gray-600">
                    Your coupon will be pinned to the top of a specific category on the shop page — the first thing customers see when filtering.
                </p>
            </div>

            <div className="row">
                {/* FORM COLUMN */}
                <div className="col-lg-7 col-xl-8">
                    <div className="border border-gray-100 rounded-16 p-40 bg-white">

                        {/* Header Info */}
                        <div className="mb-40 pb-32 border-bottom border-gray-100 d-flex align-items-center gap-16">
                            <div>
                                <h5 className="mb-8">Category-Featured Coupon Details</h5>
                                <p className="text-gray-500 mb-0">
                                    Fill out all required fields below. This coupon will be marked as{" "}
                                    <span className="fw-semibold text-main-600">Category-Featured</span> and pinned to the top of your chosen category.
                                </p>
                                <div className="mt-16 p-16 rounded-12 d-flex align-items-start gap-12" style={{ background: "#f5f3ff", border: "1px solid #ede9fe" }}>
                                    <i className="ph ph-calendar-check" style={{ fontSize: 22, color: "#7c3aed", flexShrink: 0, marginTop: 2 }} />
                                    <div>
                                        <p className="fw-semibold mb-4" style={{ color: "#5b21b6", fontSize: 14 }}>6-Month Featured Placement</p>
                                        <p className="mb-0 text-gray-500" style={{ fontSize: 13 }}>
                                            Your coupon will be featured in the selected category for <strong>6 months</strong> from the date of payment. The start and expiry dates are set automatically — no configuration needed.
                                        </p>
                                        <p className="mb-0 mt-6 fw-semibold" style={{ fontSize: 13, color: "#7c3aed" }}>$199.99 one-time payment</p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* FORM */}
                        <form onSubmit={handleCouponRegister}>
                            <div className="row gy-4">

                                {/* VENDOR NAME */}
                                <div className="col-12">
                                    <label className="mb-8 fw-medium">Vendor Name</label>
                                    <input
                                        type="text"
                                        className="common-input border-gray-100 bg-gray-50"
                                        value={vendor?.vendor_name || ""}
                                        readOnly
                                    />
                                </div>

                                {/* COUPON TYPE */}
                                <div className="col-12">
                                    <label className="mb-8 fw-medium">Coupon Type</label>
                                    <input
                                        type="text"
                                        className="common-input border-gray-100 bg-gray-50"
                                        value="Category-Featured"
                                        readOnly
                                    />
                                </div>

                                {/* FEATURED CATEGORY — the category this coupon is pinned in */}
                                <div className="col-12">
                                    <label className="mb-8 fw-medium">
                                        Featured Category <span className="text-main-600">(where your coupon will be pinned)</span>
                                    </label>
                                    <select
                                        className="common-input border-gray-100"
                                        name="featured_category"
                                        value={couponData.featured_category}
                                        onChange={handleChange}
                                    >
                                        <option value="">Select a Category to Feature In</option>
                                        {CATEGORY_OPTIONS.map((option) => (
                                            <option key={option} value={option}>{option}</option>
                                        ))}
                                    </select>
                                </div>

                                {/* BUSINESS SELECT */}
                                <div className="col-12">
                                    <label className="mb-8 fw-medium">Business</label>
                                    <select
                                        className="common-input border-gray-100"
                                        name="business_id"
                                        value={couponData.business_id}
                                        onChange={handleChange}
                                    >
                                        <option value="">Select a Business</option>
                                        {businesses.map((b) => (
                                            <option key={b.id} value={b.id}>{b.business_name}</option>
                                        ))}
                                    </select>
                                </div>

                                {/* TITLE */}
                                <div className="col-12">
                                    <label className="mb-8 fw-medium">Coupon Title</label>
                                    <input
                                        type="text"
                                        className="common-input border-gray-100"
                                        name="title"
                                        value={couponData.title}
                                        onChange={handleChange}
                                        placeholder="e.g. 20% Off Summer Sale"
                                        maxLength={35}
                                    />
                                    <p className="mb-0 mt-4" style={{ fontSize: 11.5, color: couponData.title.length >= 35 ? "#ef4444" : "#94a3b8", textAlign: "right" }}>
                                        {couponData.title.length}/35
                                    </p>
                                </div>

                                {/* DESCRIPTION */}
                                <div className="col-12">
                                    <label className="mb-8 fw-medium">Description</label>
                                    <textarea
                                        className="common-input border-gray-100"
                                        name="description"
                                        value={couponData.description}
                                        onChange={handleChange}
                                        rows="4"
                                        placeholder="Describe the coupon..."
                                        maxLength={200}
                                    />
                                    <p className="mb-0 mt-4" style={{ fontSize: 11.5, color: couponData.description.length >= 200 ? "#ef4444" : "#94a3b8", textAlign: "right" }}>
                                        {couponData.description.length}/200
                                    </p>
                                </div>

                                {/* COUPON PHOTOS */}
                                <div className="col-12">
                                    <div className="border border-gray-100 rounded-16 p-24 bg-gray-50">
                                        <h6 className="mb-4 fw-semibold text-gray-800">Coupon Photos</h6>
                                        <p className="text-gray-400 text-sm mb-24">Add up to 3 photos. The first photo appears on coupon cards across the site.</p>

                                        <div className="mb-20">
                                            <label className="mb-8 fw-medium d-block text-sm">
                                                Photo 1 — Card &amp; Detail Page <span className="text-main-600 fw-normal">(primary)</span>
                                            </label>
                                            <ImageCropUploader
                                                onUpload={(url) => setCouponData(prev => ({ ...prev, image_url: url }))}
                                                currentImageUrl={couponData.image_url}
                                            />
                                        </div>

                                        <div className="mb-20">
                                            <label className="mb-8 fw-medium d-block text-sm">
                                                Photo 2 — Detail Page Only <span className="text-gray-400 fw-normal">(optional)</span>
                                            </label>
                                            <ImageCropUploader
                                                onUpload={(url) => setCouponData(prev => ({ ...prev, image_url_2: url }))}
                                                currentImageUrl={couponData.image_url_2}
                                            />
                                        </div>

                                        <div className="mb-0">
                                            <label className="mb-8 fw-medium d-block text-sm">
                                                Photo 3 — Detail Page Only <span className="text-gray-400 fw-normal">(optional)</span>
                                            </label>
                                            <ImageCropUploader
                                                onUpload={(url) => setCouponData(prev => ({ ...prev, image_url_3: url }))}
                                                currentImageUrl={couponData.image_url_3}
                                            />
                                        </div>
                                    </div>
                                </div>

                                {/* DISCOUNT CATEGORY */}
                                <div className="col-12">
                                    <label className="mb-8 fw-medium">Discount Category</label>
                                    <select
                                        className="common-input border-gray-100"
                                        name="discount_category"
                                        value={couponData.discount_category}
                                        onChange={handleChange}
                                    >
                                        <option value="">Select Category</option>
                                        {CATEGORY_OPTIONS.map((option) => (
                                            <option key={option} value={option}>{option}</option>
                                        ))}
                                    </select>
                                </div>

                                {/* DISCOUNTS */}
                                <div className="col-md-6">
                                    <label className="mb-8 fw-medium">Saving Percentage (%)</label>
                                    <input
                                        type="number"
                                        className="common-input border-gray-100"
                                        name="saving_percentage"
                                        value={couponData.saving_percentage}
                                        onChange={handleChange}
                                        placeholder="e.g. 20"
                                    />
                                </div>

                                <div className="col-md-6">
                                    <label className="mb-8 fw-medium">Dollar Savings Amount ($)</label>
                                    <input
                                        type="number"
                                        className="common-input border-gray-100"
                                        name="dollar_savings_amount"
                                        value={couponData.dollar_savings_amount}
                                        onChange={handleChange}
                                        placeholder="e.g. 10"
                                    />
                                </div>

                                {/* LIMITS */}
                                <div className="col-12">
                                    <label className="mb-8 fw-medium">Max Redemptions Per User</label>
                                    <select
                                        className="common-input border-gray-100"
                                        name="max_redemptions_per_user"
                                        value={couponData.max_redemptions_per_user}
                                        onChange={handleChange}
                                    >
                                        {[...Array(10)].map((_, i) => (
                                            <option key={i + 1} value={i + 1}>{i + 1}</option>
                                        ))}
                                    </select>
                                </div>

                                {/* 6-MONTH TERM NOTICE */}
                                <div className="col-12">
                                    <div className="p-16 rounded-12 d-flex align-items-center gap-12" style={{ background: "#f5f3ff", border: "1px solid #ede9fe" }}>
                                        <i className="ph ph-clock" style={{ fontSize: 20, color: "#7c3aed", flexShrink: 0 }} />
                                        <p className="mb-0" style={{ fontSize: 13, color: "#5b21b6" }}>
                                            <strong>Placement duration:</strong> 6 months from payment date. Start and expiry are set automatically upon checkout completion.
                                        </p>
                                    </div>
                                </div>

                                {/* ALERTS */}
                                <div className="col-12">
                                    {error && <div className="alert alert-danger">{error}</div>}
                                </div>

                                {/* SUBMIT */}
                                <div className="col-12 mt-16 d-flex flex-wrap gap-16 align-items-center">
                                    <button
                                        type="submit"
                                        className="btn bg-main-800 text-white py-18 px-40 rounded-8"
                                        disabled={loading || !isFormComplete}
                                    >
                                        {loading ? "Processing..." : "Register Category-Featured Coupon — $199.99"}
                                    </button>
                                    <button
                                        type="button"
                                        className="btn btn-outline-main py-18 px-40 rounded-8"
                                        disabled={!isFormComplete}
                                        title={!isFormComplete ? "Fill out all required fields to preview" : "Preview coupon page"}
                                        onClick={() => setShowPreviewModal(true)}
                                    >
                                        Coupon Page Preview
                                    </button>
                                </div>

                            </div>
                        </form>

                    </div>
                </div>

                {/* PREVIEW COLUMN */}
                <div className="col-lg-5 col-xl-4 d-none d-lg-block" ref={previewColRef}>
                    <div ref={innerRef}>
                        <p className="text-gray-500 fw-medium mb-16 text-center">Category Showcase Preview</p>
                        <div className="border border-gray-100 rounded-16 p-24 bg-white">
                            <div className="d-flex align-items-center gap-8 mb-16">
                                <span className="px-10 py-4 rounded-pill fw-semibold text-white" style={{ fontSize: 11, background: "#7c3aed" }}>
                                    Category Featured
                                </span>
                                {couponData.featured_category && (
                                    <span className="text-sm fw-medium text-gray-500">{couponData.featured_category}</span>
                                )}
                            </div>
                            {couponData.image_url ? (
                                <img
                                    src={couponData.image_url}
                                    alt=""
                                    className="rounded-12 mb-16 w-100"
                                    style={{ height: 160, objectFit: 'cover' }}
                                />
                            ) : (
                                <div className="rounded-12 mb-16 d-flex align-items-center justify-content-center bg-gray-50" style={{ height: 160 }}>
                                    <i className="ph ph-image text-gray-300" style={{ fontSize: 40 }} />
                                </div>
                            )}
                            <h6 className="fw-bold mb-4" style={{ fontSize: 15 }}>
                                {couponData.title || <span className="text-gray-400 fw-normal">Coupon title</span>}
                            </h6>
                            {couponData.dollar_savings_amount && (
                                <p className="mb-0 text-main-600 fw-semibold">${couponData.dollar_savings_amount} savings · {couponData.saving_percentage}% off</p>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </section>

    {showPreviewModal && (
        <div
            onClick={(e) => { if (e.target === e.currentTarget) setShowPreviewModal(false); }}
            style={{
                position: 'fixed', inset: 0, zIndex: 9999,
                background: 'rgba(0,0,0,0.65)',
                overflowY: 'auto',
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: 'center',
                padding: '32px 16px',
            }}
        >
            <div style={{ background: 'white', borderRadius: 16, width: '100%', maxWidth: 1200, position: 'relative' }}>

                {/* Banner */}
                <div style={{ background: '#7c3aed', color: 'white', padding: '12px 24px', borderRadius: '16px 16px 0 0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span className="fw-medium">Category-Featured Coupon Preview — Not yet published</span>
                    <button
                        type="button"
                        onClick={() => setShowPreviewModal(false)}
                        style={{ background: 'none', border: 'none', color: 'white', fontSize: 22, cursor: 'pointer', lineHeight: 1 }}
                    >
                        <i className="ph ph-x" />
                    </button>
                </div>

                {/* Page content */}
                <div className="p-40">
                    <div className="row gy-4 mb-48">

                        {/* Left: images */}
                        <div className="col-xl-6">
                            <div className="product-details__left">
                                <div className="product-details__thumb-slider border border-gray-100 rounded-16">
                                    <div className="product-details__thumb flex-center p-32">
                                        <img src={previewImages[0]} alt="Preview" style={{ maxHeight: 320, objectFit: 'contain' }} />
                                    </div>
                                </div>
                                <div className="d-flex gap-8 mt-16">
                                    {previewImages.map((img, i) => (
                                        <div key={i} className="border border-gray-100 rounded-16 p-8 flex-center" style={{ width: 80, height: 80 }}>
                                            <img src={img} alt={`thumb ${i}`} style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>

                        {/* Right: details */}
                        <div className="col-xl-6">
                            <div className="product-details__content">
                                <div className="d-flex align-items-center gap-8 mb-12">
                                    <span className="px-10 py-4 rounded-pill fw-semibold text-white" style={{ fontSize: 11, background: "#7c3aed" }}>
                                        Category Featured
                                    </span>
                                    {couponData.featured_category && (
                                        <span className="text-sm fw-medium text-gray-500">{couponData.featured_category}</span>
                                    )}
                                </div>
                                <h5 className="mb-12">{couponData.title}</h5>
                                <div className="flex-align flex-wrap gap-12 mb-16">
                                    <div className="flex-align gap-4">
                                        {[1,2,3,4,5].map(star => (
                                            <span key={star} className="text-15 fw-medium d-flex text-gray-300">
                                                <i className="ph-fill ph-star" />
                                            </span>
                                        ))}
                                    </div>
                                    <span className="text-sm fw-medium text-neutral-600">No ratings yet</span>
                                    {selectedBusiness && (
                                        <>
                                            <span className="text-sm fw-medium text-gray-500">|</span>
                                            <span className="text-gray-900">{selectedBusiness.business_name}</span>
                                        </>
                                    )}
                                </div>
                                <span className="mt-32 pt-32 text-gray-700 border-top border-gray-100 d-block" />
                                <p className="text-gray-700">{couponData.description}</p>
                                <div className="my-32 flex-align gap-16 flex-wrap">
                                    <div className="flex-align gap-8">
                                        <div className="flex-align gap-8 text-main-two-600">
                                            <i className="ph-fill ph-seal-percent text-xl" />
                                            {couponData.saving_percentage}%
                                        </div>
                                        <h6 className="mb-0">${couponData.dollar_savings_amount}</h6>
                                    </div>
                                </div>
                                <span className="mt-32 pt-32 text-gray-700 border-top border-gray-100 d-block" />
                                <button
                                    type="button"
                                    disabled
                                    className="btn btn-black flex-center gap-8 rounded-8 py-16"
                                    style={{ opacity: 0.5, cursor: 'not-allowed' }}
                                >
                                    <i className="ph ph-ticket text-lg" />
                                    Redeem Coupon
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Specs */}
                    <div className="product-dContent border rounded-24">
                        <div className="product-dContent__box p-32">
                            <h6 className="mb-24">Coupon Specifications</h6>
                            <ul>
                                {previewSpecs.map(([label, value]) => (
                                    <li key={label} className="text-gray-400 mb-14 flex-align gap-14">
                                        <span className="w-20 h-20 bg-main-50 text-main-600 text-xs flex-center rounded-circle flex-shrink-0">
                                            <i className="ph ph-check" />
                                        </span>
                                        <span className="text-heading fw-medium">
                                            {label}:
                                            <div className="text-gray-500">{value || '—'}</div>
                                        </span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )}
    </>
    );
};

export default CategoryFeaturedCouponRegistration;
